import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
} from 'antd';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { axios } from 'lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';
import { OutletDetailBody, fmtDate } from 'lib/adminDetail';

const MerchantList = () => {
  const { message } = App.useApp();
  const colors = useBrandToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchMerchants = async (q = search) => {
    setLoading(true);
    try {
      const res = await axios.get('/admin/merchants', {
        params: q?.trim() ? { q: q.trim() } : {},
      });
      const list = res?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((m) =>
      `${m.full_name || ''} ${m.mobile_number || ''} ${m.email || ''} ${
        m.primaryOutlet?.name || ''
      } ${m.primaryOutlet?.city || ''} ${(m.outlets || []).map((o) => o.name).join(' ')}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const showDetail = async (record) => {
    setSelected(record);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await axios.get(`/admin/user/${record._id}`);
      const payload = res?.data || {};
      const thin =
        payload.outlets?.length > 0
          ? payload.outlets
          : record.outlets || [];
      const outletDetails = await Promise.all(
        thin.map(async (o) => {
          try {
            const d = await axios.get(`/admin/outlet/${o._id}`);
            return d?.data || { outlet: o };
          } catch (e) {
            return { outlet: o };
          }
        }),
      );
      setDetail({
        ...payload,
        outlets: outletDetails.map((d) => d.outlet || d),
        outletDetails,
      });
    } catch (e) {
      setDetail({
        personalInfo: record,
        outlets: record.outlets || [],
      });
      message.error('Could not load full merchant profile');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (record) => {
    setDeletingId(record._id);
    try {
      await axios.delete(`/admin/merchants/${record._id}`);
      message.success(
        `Deleted ${record.full_name || 'merchant'} and ${
          record.outletCount || 0
        } store(s)`,
      );
      if (selected?._id === record._id) {
        setDetailOpen(false);
        setSelected(null);
        setDetail(null);
      }
      await fetchMerchants();
    } catch (e) {
      // toast handled by axios interceptor
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'Merchant',
      key: 'merchant',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <strong>{r.full_name || '—'}</strong>
          <span style={{ color: colors.muted, fontSize: 12 }}>
            {r.mobile_number || ''}
          </span>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v) => v || '—',
    },
    {
      title: 'Stores',
      key: 'outlet',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <span>
            {r.primaryOutlet?.name || (
              <span style={{ color: colors.muted }}>No store</span>
            )}
          </span>
          <span style={{ color: colors.muted, fontSize: 12 }}>
            {r.outletCount || 0} store{(r.outletCount || 0) === 1 ? '' : 's'}
            {r.primaryOutlet?.city ? ` · ${r.primaryOutlet.city}` : ''}
          </span>
        </Space>
      ),
    },
    {
      title: 'Account',
      key: 'active',
      render: (_, r) =>
        r.active !== false ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        ),
    },
    {
      title: 'Store status',
      key: 'is_verified',
      render: (_, r) => {
        if (!r.outletCount) return <Tag>No store</Tag>;
        const pending = (r.outlets || []).filter((o) => !o.is_verified).length;
        if (pending) return <Tag color="orange">{pending} pending</Tag>;
        return <Tag color="green">Verified</Tag>;
      },
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => fmtDate(v),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Button type="primary" size="small" onClick={() => showDetail(record)}>
            View
          </Button>
          <Popconfirm
            title="Delete this merchant?"
            description="This permanently deletes the account and all their stores."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingId === record._id}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const person = detail?.personalInfo || selected;
  const stores = detail?.outlets?.length ? detail.outlets : selected?.outlets || [];

  return (
    <PageShell
      title="Merchants"
      description="Full merchant accounts, every store they own, verification, and contact details."
      actions={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => fetchMerchants()}
          loading={loading}
        >
          Refresh
        </Button>
      }
    >
      <Card style={{ borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <Input.Search
          placeholder="Search name, mobile, email, or outlet"
          allowClear
          style={{ maxWidth: 360, marginBottom: 16 }}
          onSearch={(v) => {
            setSearch(v);
            fetchMerchants(v);
          }}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Table
          rowKey={(r) => r._id}
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          onRow={(record) => ({
            onClick: () => showDetail(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title={person?.full_name || 'Merchant details'}
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        footer={[
          selected ? (
            <Popconfirm
              key="delete"
              title="Delete this merchant?"
              description="This permanently deletes the account and all their stores."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(selected)}
            >
              <Button danger loading={deletingId === selected._id}>
                Delete
              </Button>
            </Popconfirm>
          ) : null,
          <Button
            key="close"
            onClick={() => {
              setDetailOpen(false);
              setDetail(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {person && (
          <Spin spinning={detailLoading}>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Owner">{person.full_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Mobile">
                {person.mobile_number || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>
                {person.email || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Account">
                {person.active !== false ? 'Active' : 'Inactive'}
              </Descriptions.Item>
              <Descriptions.Item label="User verified">
                {person.is_verified ? 'Yes' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Joined">{fmtDate(person.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Updated">{fmtDate(person.updatedAt)}</Descriptions.Item>
            </Descriptions>
            <strong style={{ display: 'block', marginBottom: 8 }}>
              Stores ({(detail?.outletDetails || stores).length})
            </strong>
            {(detail?.outletDetails || stores.map((o) => ({ outlet: o }))).length ? (
              (detail?.outletDetails || stores.map((o) => ({ outlet: o }))).map((block, i) => {
                const o = block.outlet || block;
                return (
                  <Card key={o._id || i} size="small" style={{ marginBottom: 12 }}>
                    <OutletDetailBody
                      outlet={{ ...o, user: o.user || person }}
                      extra={
                        <>
                          <Descriptions.Item label="Coupons on store">
                            {(block.outletCoupons || []).length}
                          </Descriptions.Item>
                          <Descriptions.Item label="Happy hours">
                            {block.happyHours ? 'Yes' : 'No'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Hot offer">
                            {block.hotOffer ? 'Yes' : 'No'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Coupon titles" span={2}>
                            {(block.outletCoupons || [])
                              .map((c) => c.name || c.title)
                              .filter(Boolean)
                              .join(', ') || 'None'}
                          </Descriptions.Item>
                        </>
                      }
                    />
                  </Card>
                );
              })
            ) : (
              <span style={{ color: colors.muted }}>No stores yet.</span>
            )}
          </Spin>
        )}
      </Modal>
    </PageShell>
  );
};

export default MerchantList;
